import { toStringOrUndefined } from 'helpers'
import { CreateDocumentRequestV3, CreateDocumentRequestV3TypeEnum } from '../../../../api/models/create-document-request-v3'
import { UpdateDocumentRequestV3, UpdateDocumentRequestV3TypeEnum } from '../../../../api/models/update-document-request-v3'
import {
  CreateDocumentRequestWithFiles,
  DocumentCreate, DocumentUpdate,
  isPreviousDocumentCreate, isPreviousDocumentUpdate,
  isSupportingDocumentCreate, isSupportingDocumentUpdate,
  isTransportDocumentCreate, isTransportDocumentUpdate,
  PreviousDocumentCreate, PreviousDocumentUpdate, SupportingDocumentCreate, SupportingDocumentUpdate,
  TransportDocumentCreate, TransportDocumentUpdate, UpdateDocumentRequestWithFiles,
} from '../../document/models/request'

export function toCreateHouseConsignmentTransportDocumentRequest(
  document: TransportDocumentCreate,
  transitOperationId: number,
  houseConsignmentId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.TRANSPORT,
      transitOperationId,
      houseConsignmentId,
    },
    files: document.files,
  }
}

export function toUpdateHouseConsignmentTransportDocumentRequest(
  document: TransportDocumentUpdate,
  transitOperationId: number,
  houseConsignmentId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.TRANSPORT,
      transitOperationId,
      houseConsignmentId,
    },
    files: document.files,
  }
}

export function toCreateHouseConsignmentPreviousDocumentRequest(
  document: PreviousDocumentCreate,
  transitOperationId: number,
  houseConsignmentId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.PREVIOUS,
      referenceNumber: document.referenceNumber,
      complementOfInformation: document.complementOfInformation,
      transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
      documentType: document.documentType,
      transitOperationId,
      houseConsignmentId,
      procedureType: document.procedureType ?? undefined,
    },
    files: document.files,
  }
}

export function toUpdateHouseConsignmentPreviousDocumentRequest(
  document: PreviousDocumentUpdate,
  transitOperationId: number,
  houseConsignmentId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.PREVIOUS,
      referenceNumber: document.referenceNumber,
      complementOfInformation: document.complementOfInformation,
      transportDocumentReferenceNumber: document.transportDocumentReferenceNumber,
      documentType: document.documentType,
      transitOperationId,
      houseConsignmentId,
      procedureType: document.procedureType ?? undefined,
    },
    files: document.files,
  }
}

export function toCreateHouseConsignmentSupportingDocumentRequest(
  document: SupportingDocumentCreate,
  transitOperationId: number,
  houseConsignmentId: number | null,
): CreateDocumentRequestWithFiles {
  return {
    request: {
      complementOfInformation: document.complementOfInformation,
      documentLineItemNumber: toStringOrUndefined(document.documentLineNumber),
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: CreateDocumentRequestV3TypeEnum.SUPPORTING,
      transitOperationId,
      houseConsignmentId,
    },
    files: document.files,
  }
}

export function toUpdateHouseConsignmentSupportingDocumentRequest(
  document: SupportingDocumentUpdate,
  transitOperationId: number,
  houseConsignmentId: number | null,
): UpdateDocumentRequestWithFiles {
  return {
    request: {
      id: document.id,
      complementOfInformation: document.complementOfInformation,
      documentLineItemNumber: toStringOrUndefined(document.documentLineNumber),
      documentType: document.documentType,
      referenceNumber: document.referenceNumber,
      sequenceNumber: document.sequenceNumber,
      type: UpdateDocumentRequestV3TypeEnum.SUPPORTING,
      transitOperationId,
      houseConsignmentId,
    },
    files: document.files,
  }
}

export function toCreateDocumentRequests(
  documents: DocumentCreate[],
  transitOperationId: number,
  houseConsignmentId: number | null,
): CreateDocumentRequestV3[] {
  const transportDocumentRequests = documents.filter(isTransportDocumentCreate)
    .map((d) => toCreateHouseConsignmentTransportDocumentRequest(d, transitOperationId, houseConsignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const previousDocumentRequests = documents.filter(isPreviousDocumentCreate)
    .map((d) => toCreateHouseConsignmentPreviousDocumentRequest(d, transitOperationId, houseConsignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const supportingDocumentRequests = documents.filter(isSupportingDocumentCreate)
    .map((d) => toCreateHouseConsignmentSupportingDocumentRequest(d, transitOperationId, houseConsignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  return [
    ...transportDocumentRequests,
    ...previousDocumentRequests,
    ...supportingDocumentRequests,
  ]
}

export function toUpdateDocumentRequests(
  documents: DocumentUpdate[],
  transitOperationId: number,
  houseConsignmentId: number | null,
): UpdateDocumentRequestV3[] {
  const transportDocumentRequests = documents.filter(isTransportDocumentUpdate)
    .map((d) => toUpdateHouseConsignmentTransportDocumentRequest(d, transitOperationId, houseConsignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const previousDocumentRequests = documents.filter(isPreviousDocumentUpdate)
    .map((d) => toUpdateHouseConsignmentPreviousDocumentRequest(d, transitOperationId, houseConsignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  const supportingDocumentRequests = documents.filter(isSupportingDocumentUpdate)
    .map((d) => toUpdateHouseConsignmentSupportingDocumentRequest(d, transitOperationId, houseConsignmentId))
    .map((requestWithFiles) => requestWithFiles.request)

  return [
    ...transportDocumentRequests,
    ...previousDocumentRequests,
    ...supportingDocumentRequests,
  ]
}
